/**
 * InvestmentPortfolioChart — bubble scatter chart showing investment vs revenue.
 *
 * X-axis: Est Revenue (or SAM fallback)
 * Y-axis: Total Investment (iR&D + fR&D)
 * Bubble size: FTE allocation
 * Bubble color: Strategic Initiative
 * Quadrant labels at median values
 */

import { useMemo } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ZAxis, Cell, ReferenceLine, Label,
} from 'recharts';
import type { AdjacencyNode } from '../../types/adjacency';
import { INITIATIVE_COLORS } from '../../utils/map-colors';
import { formatCompactCurrency } from '../../utils/financial-calculations';

interface Props {
  nodes: AdjacencyNode[];
}

interface BubblePoint {
  name: string;
  initiative: string;
  revenue: number;
  investment: number;
  fte: number;
  color: string;
}

export function InvestmentPortfolioChart({ nodes }: Props) {
  const { points, medianRev, medianInv, excludedCount } = useMemo(() => {
    const pts: BubblePoint[] = [];

    for (const n of nodes) {
      const investment = (n.iRnD ?? 0) + (n.fRnD ?? 0);
      const revenue = n.estRevenue ?? n.sam ?? null;
      if (investment <= 0 || revenue == null || revenue <= 0) continue;

      pts.push({
        name: n.name,
        initiative: n.strategicInitiative,
        revenue,
        investment,
        fte: n.fteAllocation ?? 1,
        color: INITIATIVE_COLORS[n.strategicInitiative] ?? '#6b7280',
      });
    }

    // Median calculations
    const sortedRev = pts.map((p) => p.revenue).sort((a, b) => a - b);
    const sortedInv = pts.map((p) => p.investment).sort((a, b) => a - b);
    const mid = Math.floor(pts.length / 2);

    const medRev = pts.length > 0
      ? (pts.length % 2 === 0 ? (sortedRev[mid - 1] + sortedRev[mid]) / 2 : sortedRev[mid])
      : 0;
    const medInv = pts.length > 0
      ? (pts.length % 2 === 0 ? (sortedInv[mid - 1] + sortedInv[mid]) / 2 : sortedInv[mid])
      : 0;

    const excluded = nodes.length - pts.length;

    return { points: pts, medianRev: medRev, medianInv: medInv, excludedCount: excluded };
  }, [nodes]);

  if (points.length === 0) {
    return (
      <div className="glass-panel p-4 rounded-lg flex items-center justify-center h-64">
        <span className="text-xs" style={{ color: 'var(--text-dim)' }}>
          No nodes with both investment and revenue data
        </span>
      </div>
    );
  }

  return (
    <div className="glass-panel p-4 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          Investment Portfolio
        </h4>
        {excludedCount > 0 && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {excludedCount} nodes excluded (missing data)
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 30 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" />
          <XAxis
            type="number"
            dataKey="revenue"
            name="Revenue"
            tick={{ fill: '#cbd5e1', fontSize: 12 }}
            tickFormatter={(v: number) => formatCompactCurrency(v)}
            axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
          >
            <Label value="Est Revenue →" position="bottom" offset={0} style={{ fill: '#e2e8f0', fontSize: 13, fontWeight: 500 }} />
          </XAxis>
          <YAxis
            type="number"
            dataKey="investment"
            name="Investment"
            tick={{ fill: '#cbd5e1', fontSize: 12 }}
            tickFormatter={(v: number) => formatCompactCurrency(v)}
            axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
          >
            <Label value="Total Investment →" angle={-90} position="insideLeft" offset={-15} style={{ fill: '#e2e8f0', fontSize: 13, fontWeight: 500 }} />
          </YAxis>
          <ZAxis type="number" dataKey="fte" range={[40, 400]} />

          {/* Quadrant dividers at median */}
          <ReferenceLine
            x={medianRev}
            stroke="rgba(255,255,255,0.15)"
            strokeDasharray="4 4"
          />
          <ReferenceLine
            y={medianInv}
            stroke="rgba(255,255,255,0.15)"
            strokeDasharray="4 4"
          />

          <RTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0]?.payload as BubblePoint | undefined;
              if (!p) return null;
              return (
                <div style={{
                  background: 'var(--modal-bg-heavy)',
                  border: '1px solid var(--modal-border)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>
                    {p.name}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6 }}>
                    {p.initiative}
                  </div>
                  <div style={{ color: 'var(--text-primary)' }}>Investment: {formatCompactCurrency(p.investment)}</div>
                  <div style={{ color: 'var(--text-primary)' }}>Revenue: {formatCompactCurrency(p.revenue)}</div>
                  <div style={{ color: 'var(--text-primary)' }}>FTE: {p.fte.toFixed(1)}</div>
                </div>
              );
            }}
          />

          <Scatter data={points} fillOpacity={0.7} strokeWidth={1.5}>
            {points.map((p, i) => (
              <Cell key={i} fill={p.color} stroke={p.color} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      <div className="mt-2 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
        Bubble size = FTE Allocation
      </div>

      {/* Quadrant labels */}
      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
        <div>High Investment / Low Return ⚠️</div>
        <div>High Investment / High Return ✓</div>
        <div>Low Investment / Low Return</div>
        <div>Low Investment / High Return ★</div>
      </div>
    </div>
  );
}
